<div class="claim-reassignment">

  <!-- Page Header -->
  <div class="header-label">
    <h2>Claim Reassignment</h2>
  </div>

  <!-- Error Banner -->
  <div *ngIf="errorMessage" class="api-error-banner">
    {{ errorMessage }}
  </div>

  <!-- Success Banner -->
  <div *ngIf="statusMessage" class="status-banner status-success">
    {{ statusMessage }}
  </div>

  <!-- Loading Spinner -->
  <div class="table-loader" *ngIf="loading || uploading || transferring">
    <mat-spinner [diameter]="40"></mat-spinner>
  </div>

  <!-- Filter Row: Adjuster From / Adjuster To -->
  <div class="filter-row">
    <div class="filter-field">
      <mat-form-field appearance="outline" class="adjuster-select">
        <mat-label>Adjuster From</mat-label>
        <input matInput
               type="text"
               [formControl]="fromAdjusterControl"
               [matAutocomplete]="fromAdjusterAuto"
               #fromAdjusterTrigger="matAutocompleteTrigger" />
        <app-autocomplete-suffix-actions
          matSuffix
          [showClear]="fromAdjusterControl.value !== null && fromAdjusterControl.value !== ''"
          [panelOpen]="fromAdjusterTrigger.panelOpen"
          (clearClicked)="clearFromAdjuster($event)"
          (toggleClicked)="openOrClosePanel($event, fromAdjusterTrigger)">
        </app-autocomplete-suffix-actions>
        <mat-autocomplete #fromAdjusterAuto="matAutocomplete"
                          [clearStaleSelectionOnOpen]="fromAdjusterControl.value"
                          [displayWith]="displayAdjusterText"
                          (optionSelected)="onFromAdjusterSelected($event)">
          <mat-option *ngFor="let adj of filteredFromAdjusters$ | async"
                      [value]="adj">
            {{ adj.adjusterText }}
          </mat-option>
        </mat-autocomplete>
      </mat-form-field>
    </div>

    <div class="filter-field">
      <mat-form-field appearance="outline" class="adjuster-select">
        <mat-label>Adjuster To</mat-label>
        <input matInput
               type="text"
               [formControl]="toAdjusterControl"
               [matAutocomplete]="toAdjusterAuto"
               #toAdjusterTrigger="matAutocompleteTrigger" />
        <app-autocomplete-suffix-actions
          matSuffix
          [showClear]="toAdjusterControl.value !== null && toAdjusterControl.value !== ''"
          [panelOpen]="toAdjusterTrigger.panelOpen"
          (clearClicked)="clearToAdjuster($event)"
          (toggleClicked)="openOrClosePanel($event, toAdjusterTrigger)">
        </app-autocomplete-suffix-actions>
        <mat-autocomplete #toAdjusterAuto="matAutocomplete"
                          [clearStaleSelectionOnOpen]="toAdjusterControl.value"
                          [displayWith]="displayAdjusterText"
                          (optionSelected)="onToAdjusterSelected($event)">
          <mat-option *ngFor="let adj of filteredToAdjusters$ | async"
                      [value]="adj">
            {{ adj.adjusterText }}
          </mat-option>
        </mat-autocomplete>
      </mat-form-field>
    </div>
  </div>

  <!-- Reassignment Mode Radio Buttons -->
  <div class="mode-row">
    <mat-radio-group class="mode-radio-group">
      <mat-radio-button
        [checked]="reassignMode === 'all'"
        (change)="onModeChange('all')"
        value="all">
        Reassign all claims
      </mat-radio-button>
      <mat-radio-button
        [checked]="reassignMode === 'excel'"
        (change)="onModeChange('excel')"
        value="excel">
        Reassign claims on spreadsheet
      </mat-radio-button>
    </mat-radio-group>
  </div>

  <!-- Excel Upload Panel -->
  <div class="upload-panel" *ngIf="reassignMode === 'excel'">
    <div class="upload-panel-inner">
      <input #fileInput
             type="file"
             accept=".xlsx"
             class="file-input"
             (change)="onFileSelected($event)" />
      <button mat-flat-button
              color="primary"
              class="upload-btn"
              [disabled]="uploading || !selectedFile"
              (click)="onUpload()">
        Upload
      </button>
    </div>
  </div>

  <!-- Summary + Action Buttons Row -->
  <div class="summary-actions-row">
    <div class="summary-row">
      <span>Potential claims: <strong>{{ potentialClaims }}</strong></span>
      <span class="summary-sep">&nbsp;&nbsp;</span>
      <span>
        Claims to be transferred:
        <strong>{{ activeClaims }}</strong> Active claims &nbsp;
        <strong>{{ deactivatedClaims }}</strong> Deactivated claims
      </span>
    </div>

    <div class="actions-row">
      <button mat-flat-button
              color="primary"
              class="action-btn"
              [disabled]="transferring || loading"
              (click)="onTransfer()">
        Transfer
      </button>
      <button mat-stroked-button
              class="action-btn"
              (click)="onCancel()">
        Cancel
      </button>
    </div>
  </div>

  <!-- Claims Table -->
  <div class="table-wrapper" [class.loading]="loading">
    <table mat-table
           [dataSource]="dataSource"
           matSort
           [matSortActive]="sortBy"
           [matSortDirection]="sortDir"
           class="full-width claims-table">

      <ng-container matColumnDef="claimKey">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="claimKey">Claim Key</th>
        <td mat-cell *matCellDef="let row">{{ row.claimKey }}</td>
      </ng-container>

      <ng-container matColumnDef="claimantKey">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="claimantKey">Claimant Key</th>
        <td mat-cell *matCellDef="let row">{{ row.claimantKey }}</td>
      </ng-container>

      <ng-container matColumnDef="insuranceType">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="insuranceType">Insurance Type</th>
        <td mat-cell *matCellDef="let row">{{ row.insuranceType }}</td>
      </ng-container>

      <ng-container matColumnDef="claimNumber">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="claimNumber">Claim Number</th>
        <td mat-cell *matCellDef="let row">{{ row.claimNumber }}</td>
      </ng-container>

      <ng-container matColumnDef="injuredPartyName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="injuredPartyName">Injured Party Name</th>
        <td mat-cell *matCellDef="let row">{{ row.injuredPartyName }}</td>
      </ng-container>

      <ng-container matColumnDef="isDeactivated">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="isDeactivated">Deactivated Claim?</th>
        <td mat-cell *matCellDef="let row">{{ row.isDeactivated }}</td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns"
          [class.deactivated-row]="row.isDeactivated === 'Yes'"></tr>
    </table>

    <div *ngIf="dataSource.data.length === 0 && !loading" class="no-results-message">
      No claims to display.
    </div>
  </div>

  <!-- Paginator -->
  <mat-paginator
    *ngIf="showPaginator"
    [length]="paginatorLength"
    [pageIndex]="pageIndex"
    [pageSize]="pageSize"
    [pageSizeOptions]="[10, 20, 50]"
    (page)="onPageChange($event)"
    showFirstLastButtons>
  </mat-paginator>

</div>
